import { Link } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import { Breadcrumbs, FAQ, FinalCTA, GuideByline } from "../../components/seo/SEOBlocks";
import { CONTENT_LAST_UPDATED_ISO } from "../../lib/seoRegistry";

const CRUMBS = [
  { name: "Home", href: "/" },
  { name: "Guides", href: "/guides/how-long-does-an-mvp-take" },
  { name: "How Long Does an MVP Take", href: "/guides/how-long-does-an-mvp-take" },
];

const FAQS = [
  { q: "How long does a typical MVP take to build?", a: "Depends on who's building it. AI-assisted delivery services ship in 1–4 weeks. Solo freelance developers typically take 6–12 weeks. Boutique agencies take 12–24 weeks. Large agencies take 6+ months. The variation is driven far more by process overhead than by the actual coding time." },
  { q: "What's the fastest a working MVP can realistically be built?", a: "For a scoped, single-user-role internal tool: about a week of calendar time using AI-generated scaffolding and a senior-developer review pass. Anything meaningfully faster is either a prototype (clickable, not real) or a corner-cut that will cost you in the extension phase." },
  { q: "Why do agencies take so long?", a: "Three reasons: (1) they staff multiple part-time people to make the utilization economics work, (2) they add discovery / design / QA phases that duplicate work, and (3) hourly billing removes the incentive to finish quickly." },
  { q: "What causes the biggest MVP delays in practice?", a: "Almost always one of four things: unclear scope that shifts mid-project, waiting for third-party access (Stripe verification, App Store review), founder unavailability for reviews, and integrations with legacy systems that have poor documentation." },
  { q: "Can I compress the timeline if I have more budget?", a: "Not much. Adding people to a project rarely halves the time — Brooks's Law still holds. What actually compresses time is scoping less, not spending more. If you're up against a hard demo date, cut the scope, not the calendar." },
];

export default function GuideHowLongMVP() {
  return (
    <div className="min-h-screen bg-white" data-testid="page-guide-timeline">
      <Navbar />
      <section className="pt-32 pb-8 md:pt-40 md:pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <Breadcrumbs items={CRUMBS} />
          <div className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-3">Guide</div>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-zinc-950 leading-[1.05]">
            How long does it take to build an MVP?
          </h1>
          <p className="mt-6 text-lg text-zinc-600 leading-relaxed">
            A realistic breakdown of MVP timelines by build approach, why founders consistently underestimate them, and what actually compresses the calendar.
          </p>
          <GuideByline date={CONTENT_LAST_UPDATED_ISO} />
        </div>
      </section>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 prose prose-zinc prose-headings:tracking-tight prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-h2:font-bold prose-p:leading-relaxed prose-p:text-zinc-700 prose-a:text-zinc-900 prose-a:underline">
        <h2>Typical timelines by build approach</h2>
        <p>Same feature set, wildly different delivery times depending on who's holding the keyboard:</p>
        <ul>
          <li><strong>AI-assisted fixed-price delivery: 1 to 4 weeks.</strong> AI generates the scaffolding in hours; a senior developer reviews, hardens and iterates.</li>
          <li><strong>Solo freelance developer: 6 to 12 weeks.</strong> Bottlenecked by one person's availability. Longer if the freelancer has a day job.</li>
          <li><strong>Boutique agency: 12 to 24 weeks.</strong> Discovery, design, sprints, QA — each a real phase, each with its own kickoff and closeout.</li>
          <li><strong>Large agency: 6+ months.</strong> Enterprise process applied to a small project. Almost always overkill for an MVP.</li>
          <li><strong>Founder-built with no-code: highly variable.</strong> Fast if you have prior no-code experience; painful if you don't.</li>
        </ul>

        <h2>Why founders consistently underestimate</h2>
        <p>
          The most common founder estimate we hear is "I think this should take about two weeks". It almost never does — not because founders are wrong about the coding time, but because they ignore everything <em>around</em> the code:
        </p>
        <p>
          <strong>Scoping.</strong> Turning "an app where users can list gigs and get paid" into a written scope with specific entities, roles, and flows is a real day of work. If you skip it, the scoping happens mid-build, and every ambiguity becomes a change request.
        </p>
        <p>
          <strong>Third-party integrations.</strong> Stripe verification alone can take a week. Apple's App Store review is measured in days. Any B2B API you depend on will have its own onboarding process. None of this is coding time — but all of it is calendar time.
        </p>
        <p>
          <strong>Review cycles.</strong> Every meaningful feature needs at least one round of "the founder looks at it, has a reaction, we adjust". A week without review cycles is a week of guessing.
        </p>
        <p>
          <strong>Deployment reality.</strong> Getting from "runs on the developer's laptop" to "runs on a URL a stranger can hit" is half a day of DevOps work most estimates ignore.
        </p>

        <h2>What actually compresses the calendar</h2>
        <p>
          <strong>Cut scope, not corners.</strong> The only reliable way to ship faster is to ship less. A ruthless first-week scope beats an optimistic four-week scope every single time.
        </p>
        <p>
          <strong>Pre-decide the design language.</strong> Every time we build under a "design as we go" process, it adds a week. Pick a reference product, name three things you like about its UI, and let us mirror that.
        </p>
        <p>
          <strong>Pre-integrate the accounts.</strong> If your MVP needs Stripe, start the Stripe verification the day you sign the proposal. If it needs a domain, buy the domain now. Waiting for these on delivery day is the number-one cause of last-mile delays.
        </p>
        <p>
          <strong>Be available for review.</strong> The single biggest calendar accelerator is a founder who answers async within four working hours. The single biggest delay is a founder in "back next week" mode.
        </p>

        <h2>The realistic answer for your idea</h2>
        <p>
          Everything above generalises. For your specific scope, the fastest way to a real timeline is to <Link to="/build">describe the idea in the builder</Link>. You'll get a recommended package (one, two, three or four weeks) and see the delivery window before you commit.
        </p>
      </article>

      <FAQ items={FAQS} title="Timelines — quick answers" />
      <FinalCTA heading="See your delivery window" subheading="Describe your idea and receive a recommended package plus timeline in minutes." buttonLabel="Check my timeline" />
      <Footer />
    </div>
  );
}
